import React, { useEffect, useRef, useState } from 'react';
import { store as appStore } from '../../redux/store';

// Dev bellek monitörü — geometries/textures/JS-heap büyümesini izler.
// Amaç: "yenileyince düzeliyor" tarzı zamanla biriken bellek sızıntısının
// HANGİ işlemde (kat planı değişimi, tab geçişi, çiz/sil, kaydet/aç) fırladığını
// canlı görmek. Sadece development'ta (npm start) veya localStorage.memMonitor==='1'
// aktif; production build'de tamamen kapalı (sıfır maliyet).
// NOT: CRA `process.env.NODE_ENV`'i derleme anında string literal ile değiştirir
// (tarayıcıda global `process` objesi YOKTUR) → `typeof process` kontrolü koymuyoruz.
const ENABLED =
  process.env.NODE_ENV === 'development' ||
  (typeof localStorage !== 'undefined' && localStorage.getItem('memMonitor') === '1');

export default function MemoryMonitor({ renderer }) {
  const [stats, setStats] = useState(null);
  const [perf, setPerf] = useState(null);
  const [hidden, setHidden] = useState(false);
  const baselineRef = useRef(null);

  // ⏱️ Kare süresi + render süresi ölçümü — frame time'ın ne kadarı renderer.render,
  // ne kadarı başka iş (React re-render / GC / mousemove handler). renderer.render'ı
  // geçici sarar (cleanup'ta geri alır). Bottleneck'in render mi yoksa "başka yer" mi
  // olduğunu kesin gösterir.
  useEffect(() => {
    if (!ENABLED || !renderer || typeof renderer.render !== 'function') return undefined;
    const origRender = renderer.render.bind(renderer);
    let renderAccum = 0;
    let renderCount = 0;
    renderer.render = (sc, cam) => {
      const t0 = performance.now();
      origRender(sc, cam);
      renderAccum += performance.now() - t0;
      renderCount++;
    };

    let lastT = performance.now();
    let frameAccum = 0;
    let frameCount = 0;
    let rafId;
    const loop = () => {
      const now = performance.now();
      frameAccum += now - lastT;
      lastT = now;
      frameCount++;
      rafId = requestAnimationFrame(loop);
    };
    rafId = requestAnimationFrame(loop);

    // Redux dispatch sayacı — çizerken saniyede kaç dispatch (→ re-render cascade göstergesi)
    let dispatchCount = 0;
    const unsubDispatch = appStore.subscribe(() => { dispatchCount++; });

    const id = setInterval(() => {
      const avgFrame = frameCount ? frameAccum / frameCount : 0;
      const avgRender = renderCount ? renderAccum / renderCount : 0;
      const fps = avgFrame ? Math.round(1000 / avgFrame) : 0;
      const renderPct = avgFrame ? Math.round((avgRender / avgFrame) * 100) : 0;
      const dps = Math.round(dispatchCount / 2); // 2sn aralık
      setPerf({ fps, frameMs: +avgFrame.toFixed(1), renderMs: +avgRender.toFixed(1), renderPct, dps });
      // eslint-disable-next-line no-console
      console.log(`[PERF] fps=${fps} frame=${avgFrame.toFixed(1)}ms render=${avgRender.toFixed(1)}ms (render=${renderPct}%, other=${100 - renderPct}%) dispatch=${dps}/sn`);
      frameAccum = 0; frameCount = 0; renderAccum = 0; renderCount = 0; dispatchCount = 0;
    }, 2000);

    return () => {
      clearInterval(id);
      cancelAnimationFrame(rafId);
      try { unsubDispatch(); } catch (_) {}
      renderer.render = origRender; // geri al
    };
  }, [renderer]);

  useEffect(() => {
    if (!ENABLED || !renderer) return undefined;

    const read = () => {
      const info = renderer.info || {};
      const mem = info.memory || {};
      const render = info.render || {};
      const heap = (typeof performance !== 'undefined' && performance.memory) ? performance.memory : null;
      const s = {
        geometries: mem.geometries ?? 0,
        textures: mem.textures ?? 0,
        programs: info.programs?.length ?? 0,
        calls: render.calls ?? 0,
        triangles: render.triangles ?? 0,
        heapMB: heap ? Math.round(heap.usedJSHeapSize / 1048576) : null,
        heapLimitMB: heap ? Math.round(heap.jsHeapSizeLimit / 1048576) : null,
      };
      if (!baselineRef.current) {
        baselineRef.current = { geometries: s.geometries, textures: s.textures, heapMB: s.heapMB ?? 0 };
      }
      s.dGeo = s.geometries - baselineRef.current.geometries;
      s.dTex = s.textures - baselineRef.current.textures;
      s.dHeap = s.heapMB != null ? s.heapMB - baselineRef.current.heapMB : null;
      return s;
    };

    const tick = () => {
      const s = read();
      setStats(s);
      // eslint-disable-next-line no-console
      console.log(
        `[MEM] geo=${s.geometries}(+${s.dGeo}) tex=${s.textures}(+${s.dTex}) prog=${s.programs} ` +
        `heap=${s.heapMB ?? '?'}MB(+${s.dHeap ?? '?'}) calls=${s.calls} tri=${s.triangles}`
      );
    };

    tick();
    const id = setInterval(tick, 5000);
    return () => clearInterval(id);
  }, [renderer]);

  if (!ENABLED || hidden || !stats) return null;

  const warn = stats.heapMB != null && stats.heapLimitMB != null && stats.heapMB / stats.heapLimitMB > 0.8;
  const dColor = (d) => (d > 0 ? '#fc6' : '#6c6');

  return (
    <div style={{
      position: 'fixed', bottom: 8, left: 8, zIndex: 99999,
      background: warn ? 'rgba(120,20,20,0.9)' : 'rgba(20,20,20,0.85)',
      color: '#9f9', font: '11px/1.45 monospace', padding: '6px 8px',
      borderRadius: 4, userSelect: 'none', pointerEvents: 'auto',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, marginBottom: 2 }}>
        <b style={{ color: '#fff' }}>MEM</b>
        <span onClick={() => setHidden(true)} style={{ cursor: 'pointer', color: '#f88' }} title="Gizle">×</span>
      </div>
      <div>geo: {stats.geometries} <span style={{ color: dColor(stats.dGeo) }}>(+{stats.dGeo})</span></div>
      <div>tex: {stats.textures} <span style={{ color: dColor(stats.dTex) }}>(+{stats.dTex})</span></div>
      <div>prog: {stats.programs}</div>
      {stats.heapMB != null && (
        <div>heap: {stats.heapMB}/{stats.heapLimitMB}MB <span style={{ color: dColor(stats.dHeap) }}>(+{stats.dHeap})</span></div>
      )}
      <div style={{ color: '#888' }}>calls {stats.calls} · tri {stats.triangles}</div>
      {perf && (
        <div style={{ marginTop: 3, paddingTop: 3, borderTop: '1px solid #333' }}>
          <div style={{ color: perf.fps < 30 ? '#f88' : '#9f9' }}>
            {perf.fps} fps · frame {perf.frameMs}ms
          </div>
          <div style={{ color: '#8cf' }}>
            render {perf.renderMs}ms ({perf.renderPct}%) · diğer {100 - perf.renderPct}%
          </div>
          <div style={{ color: perf.dps > 20 ? '#fc6' : '#888' }}>
            dispatch {perf.dps}/sn
          </div>
        </div>
      )}
    </div>
  );
}
